<template>
  <DashboardTopCardContent
    :callToActionButtonText="$t('L_view_details')"
    @callToActionCallback="goToInvestments"
  >
    <template #main-content>
      <div class="d-flex align-center justify-center">
        <div>
          <h2 class="text-h2 font-weight-bold">
            {{ $t('L_total_value') }}
            <v-tooltip v-if="multipleAccounts" location="top" max-width="200">
              <template v-slot:activator="{ props }">
                <v-icon color="blue-darken-2" size="x-small" v-bind="props" :icon="mdiHelpCircle" />
              </template>
              <span>{{ $t('L_total_help_tooltip') }}</span>
            </v-tooltip>
          </h2>
          <h5 v-if="!multipleAccounts" class="font-weight-medium text-center">
            {{ $t('L_as_of') }} {{ summary?.dataAsOf }}
          </h5>
        </div>
        <h2 class="font-weight-bold" :class="$vuetify.display.mdAndUp ? 'ml-8' : 'ml-6'">
          {{ currency(summary?.totalValue ?? 0) }}
        </h2>
      </div>
    </template>
    <template #bottom-content>
      <v-row class="align-center">
        <v-col>
          <p v-if="isCanadian">
            {{ $t('L_important_disclosures_ca') }}
            <span class="link font-weight-bold" @click="openDisclosure()">
              {{ $t('L_here') }}
            </span>
          </p>
          <p v-else>
            {{ $t('L_important_disclosures_us') }}
            <span class="link font-weight-bold" @click="openDisclosure()">
              {{ $t('L_important_disclosures') }}
            </span>
          </p>
        </v-col>
      </v-row>
    </template>
  </DashboardTopCardContent>
  <EmbeddedViewer
    :title="document.title"
    :loading="document.loading"
    :content-type="document.type"
    :content-url="document.content"
    @close="document.content = null"
  />
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { COUNTRIES } from '@/shared/models/constants.models';
import { currency, percentage } from '@/mixins/numbers';
import DashboardTopCardContent from '../../DashboardTopCardContent.vue';
import EmbeddedViewer from '@/components/EmbeddedViewer.vue';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { mdiHelpCircle } from '@mdi/js';
import { mapStores } from 'pinia';
import { $t } from '@/i18n/i18n.ts';
import { RouteNames } from '@/router/routeNames';

export default defineComponent({
  name: 'InvestmentsSummary',
  mixins: [currency, percentage],
  components: { DashboardTopCardContent, EmbeddedViewer },
  data() {
    return {
      mdiHelpCircle,
      document: {
        title: '',
        content: null as string | null,
        type: '',
        loading: false,
      },
    };
  },
  computed: {
    ...mapStores(useCustomerStore),
    multipleAccounts() {
      if (this.customerStore.investments.groups === undefined) {
        return false;
      }
      const totalAccounts = this.customerStore.investments.groups
        .map((group) => group.accounts?.length)
        .reduce((total = 0, current = 0) => total + current);
      return totalAccounts ? totalAccounts > 1 : false;
    },
    summary() {
      return this.customerStore.investments.summary;
    },
    isCanadian() {
      return String(this.customerStore.customerProfile.country) === String(COUNTRIES.CANADA);
    },
  },
  methods: {
    $t,
    async goToInvestments() {
      await (this.$router as { push: (location: { name: string }) => Promise<void> }).push({
        name: RouteNames.INVESTMENTS,
      });
    },
    openDisclosure() {
      this.document.type = 'application/pdf';
      this.document.title = this.$t('L_important_disclosures');
      if (this.customerStore.customerProfile.country.toLowerCase() === 'ca') {
        this.document.content = `${window.appConfig.content_stack}${window.appConfig.investments.important_disclosures.ca}`;
      } else {
        this.document.content = `${window.appConfig.content_stack}${window.appConfig.investments.important_disclosures.us}`;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.link {
  color: #02579e;
  cursor: pointer;
}
</style>
